"use client";

import { useMemo, useRef, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import { ResultsPanel } from "@/components/tools/stats-clinical/bland-altman-results";
import { PasteBox } from "@/components/tools/stats-research/paste-box";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type AgreementProblem,
  type AgreementScale,
  agreement,
  checkPairs,
} from "@/lib/tool-kit/stats-clinical/bland-altman";
import {
  type HeaderMode,
  parseNumericTable,
} from "@/lib/tool-kit/stats-research/matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const BLAND_ALTMAN_PLOT_MAKER_STRINGS = {
  dataLabel:
    "Paired measurements — one subject per row: method A, then method B",
  dataHint:
    "Paste two columns from a spreadsheet, or separate the values with tabs, commas or spaces. A header row is optional; its two names label the plot. A row with a missing value is left out.",
  dataPlaceholder: "494\t512\n395\t430\n516\t520",
  dataExample:
    "Wright\tMini Wright\n494\t512\n395\t430\n516\t520\n434\t428\n476\t500\n557\t600\n413\t364\n442\t380\n650\t658\n433\t445\n417\t432\n656\t626\n267\t260\n478\t477\n178\t259\n423\t350\n427\t451",
  exampleUnit: "l/min",
  loadExample: "Load example",
  clear: "Clear",
  headerLabel: "Header row",
  headerAuto: "Detect automatically",
  headerYes: "First row is a header",
  headerNo: "No header row",
  nameA: "Name of method A (optional)",
  nameB: "Name of method B (optional)",
  unitLabel: "Unit of measurement (optional)",
  defaultA: "Method A",
  defaultB: "Method B",
  scaleLabel: "What to plot",
  scaleDifference: "Differences, A − B",
  scalePercent: "Percentage differences",
  scaleRatio: "Ratios (log scale)",
  scaleHint:
    "Use percentage differences or ratios when the spread of the differences grows with the size of the measurement.",
  showBands: "Shade the confidence intervals of the bias and the limits",
  showTrend: "Show the regression of the differences on the means",
  showRegressionLimits: "Show regression-based limits of agreement",
  acceptableLabel: "Largest acceptable difference (optional)",
  acceptableHint:
    "Decide this on clinical or scientific grounds before looking at the data. Drawn as dotted lines at plus and minus this value (differences and percentages only).",
  pairsRead: "one: {count} pair|other: {count} pairs",
  droppedRows:
    "one: {count} row with a missing value was left out.|other: {count} rows with a missing value were left out.",
  needTwoColumns: "Enter two columns: one value from each method on every row.",
  unreadable: "Line {line}, column {column}: “{text}” is not a number.",
  problemFew: "Enter at least three pairs of measurements.",
  problemNonPositive:
    "The ratio scale uses logarithms, so every measurement must be above zero.",
  problemZeroMean:
    "Percentage differences divide by the mean of each pair, which is zero in at least one pair.",
  problemNoSpread:
    "Every difference is identical, so there is no spread to build limits of agreement from.",
  enterPrompt:
    "Paste paired measurements from two methods to draw the Bland–Altman plot and get the bias and limits of agreement.",
  results: "Results",
  plotLabel: "Bland–Altman plot of {n} pairs of measurements",
  xMean: "Mean of {a} and {b}",
  yDifference: "{a} − {b}",
  yPercent: "({a} − {b}) as % of the mean",
  yRatio: "ln({a}) − ln({b})",
  meanLine: "Mean {value}",
  upperLine: "+1.96 SD {value}",
  lowerLine: "−1.96 SD {value}",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  rowBias: "Mean difference (bias)",
  rowLower: "Lower limit of agreement",
  rowUpper: "Upper limit of agreement",
  rowRatio: "Geometric mean ratio (A / B)",
  rowLowerRatio: "Lower limit, ratio",
  rowUpperRatio: "Upper limit, ratio",
  rowSd: "SD of the differences (plotted scale)",
  rowSlope: "Slope of difference on mean",
  columnMeasure: "Measure",
  columnEstimate: "Estimate",
  columnInterval: "95% CI",
  reading:
    "On average {a} differs from {b} by {bias} (95% CI {biasLow} to {biasHigh}). About 95% of differences between the two methods are expected to fall between {lower} and {upper}; points outside the limits: {outside} of {n}.",
  readingRatio:
    "On average {a} reads {bias} times {b} (95% CI {biasLow} to {biasHigh}). About 95% of ratios are expected to fall between {lower} and {upper}; points outside the limits: {outside} of {n}.",
  trendYes:
    "The differences change with the size of the measurement (slope {slope}, p {p}). The single bias and constant limits may mislead — try percentage differences, the ratio scale, or regression-based limits.",
  trendNo:
    "No evidence that the differences depend on the size of the measurement (slope {slope}, p {p}). With few pairs, absence of evidence is weak reassurance.",
  verdictWithin:
    "Both limits of agreement, with their confidence intervals, lie within ±{limit}: the methods agree acceptably by your criterion.",
  verdictUncertain:
    "The limits of agreement lie within ±{limit}, but their confidence intervals reach beyond it: the sample cannot settle whether agreement is acceptable.",
  verdictOutside:
    "At least one limit of agreement lies beyond ±{limit}: the methods do not agree within your criterion.",
  howCalculated: "How this was calculated",
  workingBias: "Bias d̄ = {bias}, SD of the differences s = {sd}, n = {n}",
  workingLimits: "Limits of agreement = d̄ ± 1.96 × s = {lower} to {upper}",
  workingCi:
    "95% CIs use t = {t} with {df} degrees of freedom: bias d̄ ± t·s/√n; each limit ± t·s·√(1/n + 1.96²/(2(n − 1))) (Bland & Altman 1999).",
  workingNote:
    "The regression-based limits regress the differences on the means, regress the absolute residuals on the means, and draw the line ± 1.96 × √(π/2) × the fitted residual size (Bland & Altman 1999, section 4).",
  report:
    "Bland–Altman analysis (n = {n}): bias {bias} (95% CI {biasLow} to {biasHigh}); 95% limits of agreement {lower} to {upper}.",
  copyResult: "Copy result",
  copied: "Copied!",
} as const;

const SCALES: readonly {
  value: AgreementScale;
  key: "scaleDifference" | "scalePercent" | "scaleRatio";
}[] = [
  { value: "difference", key: "scaleDifference" },
  { value: "percent", key: "scalePercent" },
  { value: "ratio", key: "scaleRatio" },
];

const PROBLEM_KEY: Record<
  AgreementProblem,
  "problemFew" | "problemNonPositive" | "problemZeroMean" | "problemNoSpread"
> = {
  few: "problemFew",
  nonPositive: "problemNonPositive",
  zeroMean: "problemZeroMean",
  noSpread: "problemNoSpread",
};

export default function BlandAltmanPlotMakerWidget() {
  const strings = useToolUi(BLAND_ALTMAN_PLOT_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [text, setText] = useState("");
  const [header, setHeader] = useState<HeaderMode>("auto");
  const [nameA, setNameA] = useState("");
  const [nameB, setNameB] = useState("");
  const [unit, setUnit] = useState("");
  const [scale, setScale] = useState<AgreementScale>("difference");
  const [bands, setBands] = useState(true);
  const [trend, setTrend] = useState(false);
  const [regressionLimits, setRegressionLimits] = useState(false);
  const [acceptableText, setAcceptableText] = useState("");

  const table = useMemo(() => parseNumericTable(text, header), [text, header]);
  const pairs = useMemo(
    () =>
      table.rows.flatMap((row) =>
        row.length >= 2 && row[0] !== null && row[1] !== null
          ? [{ a: row[0], b: row[1] }]
          : []
      ),
    [table]
  );
  const dropped = table.rows.length - pairs.length;
  const entered = text.trim() !== "";
  const problem = table.columns >= 2 ? checkPairs(pairs, scale) : null;
  const result = useMemo(
    () => (table.columns >= 2 && !problem ? agreement(pairs, scale) : null),
    [table.columns, problem, pairs, scale]
  );
  const acceptableRaw = parseNumberInput(acceptableText);
  const acceptable =
    acceptableRaw !== null && acceptableRaw > 0 ? acceptableRaw : null;
  const names = {
    a: nameA.trim() || table.header?.[0] || strings.defaultA,
    b: nameB.trim() || table.header?.[1] || strings.defaultB,
    unit: unit.trim(),
  };
  const [firstProblem] = table.unreadable;

  return (
    <div className="space-y-6">
      <PasteBox
        actions={[
          {
            label: strings.loadExample,
            onClick: () => {
              setHeader("auto");
              setText(strings.dataExample);
              setUnit(strings.exampleUnit);
              setNameA("");
              setNameB("");
            },
          },
          { label: strings.clear, onClick: () => setText("") },
        ]}
        header={{
          label: strings.headerLabel,
          value: header,
          onChange: setHeader,
          options: [
            { value: "auto", label: strings.headerAuto },
            { value: "yes", label: strings.headerYes },
            { value: "no", label: strings.headerNo },
          ],
        }}
        hint={strings.dataHint}
        id="ba-data"
        label={strings.dataLabel}
        onChange={setText}
        placeholder={strings.dataPlaceholder}
        rows={8}
        value={text}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="ba-name-a"
          label={strings.nameA}
          onChange={setNameA}
          value={nameA}
        />
        <TextField
          id="ba-name-b"
          label={strings.nameB}
          onChange={setNameB}
          value={nameB}
        />
        <TextField
          id="ba-unit"
          label={strings.unitLabel}
          onChange={setUnit}
          value={unit}
        />
      </div>
      <PillGroup
        label={strings.scaleLabel}
        name="ba-scale"
        onChange={setScale}
        options={SCALES.map(({ value, key }) => ({
          value,
          label: strings[key],
        }))}
        value={scale}
      />
      <p className={HINT}>{strings.scaleHint}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Checkbox
            checked={bands}
            label={strings.showBands}
            onChange={setBands}
          />
          <Checkbox
            checked={trend}
            label={strings.showTrend}
            onChange={setTrend}
          />
          <Checkbox
            checked={regressionLimits}
            label={strings.showRegressionLimits}
            onChange={setRegressionLimits}
          />
        </div>
        <TextField
          hint={strings.acceptableHint}
          id="ba-acceptable"
          label={strings.acceptableLabel}
          numeric
          onChange={setAcceptableText}
          value={acceptableText}
        />
      </div>
      {entered && pairs.length > 0 ? (
        <p className={HINT}>
          {[
            tPlural(strings.pairsRead, locale, pairs.length, {
              count: fmt(pairs.length),
            }).trim(),
            dropped > 0
              ? tPlural(strings.droppedRows, locale, dropped, {
                  count: fmt(dropped),
                }).trim()
              : "",
          ]
            .filter((part) => part !== "")
            .join(" ")}
        </p>
      ) : null}
      {firstProblem ? (
        <ErrorNotice>
          {t(strings.unreadable, {
            line: fmt(firstProblem.line),
            column: fmt(firstProblem.column),
            text: firstProblem.text,
          })}
        </ErrorNotice>
      ) : null}
      {entered && table.columns < 2 && !firstProblem ? (
        <ErrorNotice>{strings.needTwoColumns}</ErrorNotice>
      ) : null}
      {problem && !firstProblem ? (
        <ErrorNotice>{strings[PROBLEM_KEY[problem]]}</ErrorNotice>
      ) : null}
      {result && !firstProblem ? (
        <ResultsPanel
          fmt={fmt}
          names={names}
          options={{ bands, trend, regressionLimits, acceptable }}
          result={result}
          strings={strings}
          svgRef={svgRef}
        />
      ) : null}
      {entered ? null : <Notice>{strings.enterPrompt}</Notice>}
    </div>
  );
}
